// src/utils/portalRedirect.js
//
// portalredir-2e9b6d38 — S3, security audit 2026-10-02 finding #6.
//
// PortalLogin used to do `navigate(searchParams.get('redirect') || '/portal/quotes')` with no
// check. react-router treats `//evil.com` as a path, history.pushState then throws a cross-
// origin error, and react-router's fallback is `window.location.assign(url)` — a full
// navigation to evil.com from a trusted app.spatchy.co link. Anyone with any valid portal key
// (every customer has one) could craft such a link.
//
// RULE: the redirect must be a same-site path INSIDE the customer portal. Anything else —
// absent, malformed, external, scheme-bearing, protocol-relative, backslash or encoded-slash
// tricks, dot-segments, control characters, or the login page itself — falls back to the
// portal home. It never throws.
//
// The server only ever builds `/portal/invoices` and `/portal/quotes/{quoteId}`
// (functions/index.js portal links), and both pass.

export const PORTAL_HOME = '/portal/quotes';

const MAX_LEN = 512;

/**
 * @param {unknown} raw   the ?redirect= value (already URL-decoded once by URLSearchParams)
 * @param {string} [fallback]
 * @returns {string} a safe in-portal path
 */
export function safePortalRedirect(raw, fallback = PORTAL_HOME) {
    if (typeof raw !== 'string') return fallback;
    const p = raw;
    if (!p || p.length > MAX_LEN) return fallback;

    // Must be a site-relative path inside the portal: "/portal", "/portal/…", "/portal?…".
    if (!(p === '/portal' || p.startsWith('/portal/') || p.startsWith('/portal?'))) return fallback;

    // No backslashes (browsers read "\" as "/"), no whitespace or control characters
    // (browsers strip tabs/newlines inside URLs), no "//" anywhere, no scheme separator.
    if (/[\\\s]/.test(p)) return fallback;
    for (let i = 0; i < p.length; i += 1) {
        const c = p.charCodeAt(i);
        if (c < 0x20 || c === 0x7f) return fallback; // control characters
    }
    if (p.includes('//')) return fallback;
    if (p.includes(':')) return fallback;

    // Encoded slash / backslash / percent (catches double-encoded "%252F%252F" too) and
    // encoded control characters.
    if (/%(2f|5c|25|0[0-9a-f]|1[0-9a-f]|7f)/i.test(p)) return fallback;

    // No "." or ".." path segments (path part only).
    const pathPart = p.split(/[?#]/)[0];
    if (pathPart.split('/').some((seg) => seg === '.' || seg === '..')) return fallback;

    // Belt and braces: resolve it the way a browser would and require it to stay on this
    // origin and inside /portal.
    try {
        const base = 'https://portal-redirect.invalid';
        const u = new URL(p, base);
        if (u.origin !== base) return fallback;
        if (!(u.pathname === '/portal' || u.pathname.startsWith('/portal/'))) return fallback;
    } catch {
        return fallback;
    }

    // Never back into the login page (would loop, and would carry a key around).
    if (pathPart === '/portal/login' || pathPart.startsWith('/portal/login/')) return fallback;

    return p;
}
